/**
 * Threat (PLAN §3, TC ThreatManager): 1 threat per damage, 0.5 per healing/resource point
 * split across the enemies fighting the healed unit, tank multiplier from auras, taunt
 * with DR, and victim selection (fixate > taunt > threat with 110% melee / 130% ranged).
 */
import type { Ms, SimContext, ThreatEntry, Unit, UnitId } from '../contracts';
import { DR_RESET_MS, TAUNT_DR_MULTIPLIERS, THREAT_OVERTAKE_MELEE, THREAT_OVERTAKE_RANGED, THREAT_RESELECT_MS } from '../contracts';
import { unitEffectiveStats } from './stats';
import { isHostile, unitOf } from './util';

/** Healing / energize threat per point before the tank multiplier. */
export const HEALING_THREAT_FACTOR = 0.5;

function entryFor(npc: Unit, sourceId: UnitId, now: Ms): ThreatEntry {
  const t = npc.threat!;
  let e = t.entries[sourceId];
  if (!e) {
    e = { threat: 0, lastAt: now };
    t.entries[sourceId] = e;
  }
  return e;
}

export function addThreat(ctx: SimContext, npcId: UnitId, sourceId: UnitId, amount: number): void {
  const npc = unitOf(ctx, npcId);
  const src = unitOf(ctx, sourceId);
  if (!npc?.alive || !npc.threat || !src?.alive || !isHostile(npc, src)) return;
  if (npc.ai?.mode === 'evading') return;
  const e = entryFor(npc, sourceId, ctx.now);
  e.threat = Math.max(0, e.threat + amount * unitEffectiveStats(ctx, src).threatMult);
  e.lastAt = ctx.now;
  npc.inCombat = true;
  src.inCombat = true;
  // First unit on an empty table becomes the victim right away (TC: initial aggro).
  if (!npc.threat.victimId) {
    npc.threat.victimId = sourceId;
    npc.targetId = sourceId;
  }
}

/** NPCs (alive, in combat) that have `unitId` on their threat table. */
export function enemiesFighting(ctx: SimContext, unitId: UnitId): Unit[] {
  const out: Unit[] = [];
  for (const id of ctx.world.unitOrder) {
    const n = ctx.world.units[id];
    if (n?.alive && n.threat && n.inCombat && n.threat.entries[unitId]) out.push(n);
  }
  return out;
}

export function addHealingThreat(ctx: SimContext, sourceId: UnitId, healedId: UnitId, amount: number): void {
  const src = unitOf(ctx, sourceId);
  if (!src?.alive || amount <= 0) return;
  const enemies = enemiesFighting(ctx, healedId).filter((n) => isHostile(n, src));
  if (enemies.length === 0) return;
  const share = (amount * HEALING_THREAT_FACTOR) / enemies.length;
  for (const n of enemies) addThreat(ctx, n.id, sourceId, share);
}

/** Multiply (or zero) a source's existing threat on an NPC (threat effect `multiply`). */
export function scaleThreat(ctx: SimContext, npcId: UnitId, sourceId: UnitId, factor: number): void {
  const e = unitOf(ctx, npcId)?.threat?.entries[sourceId];
  if (e) e.threat = Math.max(0, e.threat * factor);
}

/**
 * Taunt: taunter's threat is raised to the top value and it is forced as the victim for
 * the (DR-reduced) duration. Latest taunt wins. Returns the applied duration (0 = immune).
 */
export function taunt(ctx: SimContext, npcId: UnitId, taunterId: UnitId, durationMs: Ms): Ms {
  const npc = unitOf(ctx, npcId);
  const by = unitOf(ctx, taunterId);
  if (!npc?.alive || !npc.threat || !by?.alive || !isHostile(npc, by)) return 0;
  // Evading NPCs ignore taunts (same guard as dealDamage).
  if (isEvading(ctx, npc)) return 0;
  const t = npc.threat;
  let dur = durationMs;
  if (npc.tauntDr) {
    if (ctx.now >= t.tauntDrResetAt) t.tauntDrCount = 0;
    const m = TAUNT_DR_MULTIPLIERS[t.tauntDrCount] ?? 0;
    if (m <= 0) return 0;
    dur = Math.round(dur * m);
    t.tauntDrCount += 1;
    t.tauntDrResetAt = ctx.now + dur + DR_RESET_MS;
  }
  let top = 0;
  for (const e of Object.values(t.entries)) top = Math.max(top, e.threat);
  const e = entryFor(npc, taunterId, ctx.now);
  e.threat = Math.max(e.threat, top);
  e.lastAt = ctx.now;
  t.taunt = { by: taunterId, until: ctx.now + dur };
  if (!fixateTarget(ctx, npc)) {
    t.victimId = taunterId;
    npc.targetId = taunterId;
  }
  npc.inCombat = true;
  by.inCombat = true;
  ctx.sys.ai.onAttacked(ctx, npcId, taunterId);
  return dur;
}

/** Evading / damage-immune NPCs take no threat and can't be taunted. */
function isEvading(ctx: SimContext, npc: Unit): boolean {
  return npc.ai?.mode === 'evading' || ctx.sys.combat.controlState(ctx, npc.id).immuneDamage;
}

function validVictim(ctx: SimContext, npc: Unit, id: UnitId | null | undefined): boolean {
  const u = unitOf(ctx, id);
  return !!u && u.alive && isHostile(npc, u);
}

function fixateTarget(ctx: SimContext, npc: Unit): UnitId | null {
  const f = npc.threat?.fixate;
  return f && f.until > ctx.now && validVictim(ctx, npc, f.on) ? f.on : null;
}

/**
 * Pure victim choice (no mutation): fixate > taunt > threat. A challenger replaces the
 * current victim only with > 110% of its threat in melee range or > 130% at range.
 */
export function pickVictim(ctx: SimContext, npcId: UnitId): UnitId | null {
  const npc = unitOf(ctx, npcId);
  const t = npc?.threat;
  if (!npc?.alive || !t) return null;
  const fix = fixateTarget(ctx, npc);
  if (fix) return fix;
  if (t.taunt && t.taunt.until > ctx.now && validVictim(ctx, npc, t.taunt.by)) return t.taunt.by;

  return walkThreatList(ctx, npc, (id) => validVictim(ctx, npc, id));
}

/**
 * TrinityCore ReselectVictim: walk the valid threat entries sorted by threat (desc, ties in
 * unitOrder). Reaching the current victim keeps it; an earlier entry takes over with > 130%
 * of the current victim's threat, or > 110% when it is in melee range. Shared by AI and combat.
 */
export function walkThreatList(ctx: SimContext, npc: Unit, valid: (id: UnitId) => boolean): UnitId | null {
  const t = npc.threat;
  if (!t) return null;
  const list: { id: UnitId; threat: number }[] = [];
  for (const id of ctx.world.unitOrder) {
    const e = t.entries[id];
    if (e && valid(id)) list.push({ id, threat: e.threat });
  }
  if (list.length === 0) return null;
  list.sort((a, b) => b.threat - a.threat); // stable: ties keep unitOrder
  const cur = t.victimId;
  const curEntry = cur ? list.find((x) => x.id === cur) : undefined;
  if (!cur || !curEntry) return list[0]!.id;
  for (const c of list) {
    if (c.id === cur) return cur;
    if (c.threat > curEntry.threat * THREAT_OVERTAKE_RANGED) return c.id;
    if (c.threat > curEntry.threat * THREAT_OVERTAKE_MELEE && ctx.sys.spatial.inMeleeRange(ctx.world, npc.id, c.id)) return c.id;
  }
  return cur;
}

/**
 * Re-pick the victim at most every THREAT_RESELECT_MS (or now if `force`), clearing
 * expired taunt/fixate. Updates threat.victimId and the NPC's targetId.
 */
export function reselectVictim(ctx: SimContext, npcId: UnitId, force = false): UnitId | null {
  const npc = unitOf(ctx, npcId);
  const t = npc?.threat;
  if (!npc || !t) return null;
  if (!force && ctx.now < t.nextReselectAt) return t.victimId;
  if (t.taunt && t.taunt.until <= ctx.now) t.taunt = null;
  if (t.fixate && t.fixate.until <= ctx.now) t.fixate = null;
  const v = pickVictim(ctx, npcId);
  t.victimId = v;
  npc.targetId = v;
  t.nextReselectAt = ctx.now + THREAT_RESELECT_MS;
  return v;
}
